// PayPay銀行の入出金明細CSVをZeniの取引インポート形式に変換する。
//
// 銀行のWeb（入出金明細 > CSVダウンロード）から取得できるCSVは Shift-JIS で、次の列を持つ:
//   操作日(年), 操作日(月), 操作日(日), 操作時刻(時), 操作時刻(分), 操作時刻(秒),
//   取引順番号, 摘要, お支払金額, お預り金額, 残高, メモ
// 他行のCSVと違い日付が年・月・日の3列に分かれ、月日はゼロ埋めされない（"2021","9","27"）。
// 「お支払金額」のある行を支出、「お預り金額」のある行を収入として取り込む。
// 操作時刻と取引順番号（同日内の連番）は日付単位のZeniの取引では使わない。
// 残高列は口座残高の再計算には使わない（取り込んだ取引の差分で口座残高が更新されるため、
// 古い期間のCSVを取り込んだときに残高が巻き戻るのを避ける）。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  normalizeForMatch,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type PayPayBankCategoryRef = CsvCategoryRef
export type PayPayBankImportRow = CsvImportRow
export type PayPayBankParseResult = CsvParseResult

// 店名キーワードより先に判定する銀行固有の摘要。
// PayPay銀行の摘要は振込先名や公金の名称が全角カナでそのまま入るため、
// 店名表（「セブン」＝食費 など）に引っ張られる前にここで確定させる。
const BANK_EXPENSE_OVERRIDE: ReadonlyArray<readonly [string, string]> = [
  // Visaデビットの摘要は「Visaデビット(1A270003)」のように承認番号だけで店名が入らない。
  // 何のカテゴリにも寄せられないので「その他」で確定させる。
  ['Visaデビット', 'other'], ['ＶＩＳＡデビツト', 'other'],
  ['ＡＴＭ', 'other'], ['ATM', 'other'], ['カード引出', 'other'], ['引出', 'other'],
  ['振込手数料', 'other'], ['手数料', 'other'],
  // 税金・公金。Pay-easy 払いの摘要は「PE ヒガシムラヤマゼイムシヨ」のようにカナ書きになる
  ['住民税', 'other'], ['所得税', 'other'], ['国税', 'other'], ['地方税', 'other'], ['税金', 'other'],
  ['シミンゼイ', 'other'], ['トミンゼイ', 'other'], ['ケンミンゼイ', 'other'], ['ジユウミンゼイ', 'other'],
  ['ゼイムシヨ', 'other'], ['税務署', 'other'], ['年金', 'other'], ['ネンキン', 'other'],
  ['社会保険料', 'other'], ['シヤカイホケン', 'other'], ['国庫金', 'other'],
  // 他行への振込は摘要が相手銀行名になる。店名表に載る名前を含む銀行
  // （ドコモＳＭＴＢ→通信費、イオン銀行→食費 など）へ誤分類されるのを防ぐ。
  ['銀行', 'other'], ['ギンコウ', 'other'], ['ドコモSMTB', 'other'], ['SMTBネツト', 'other'],
  ['スミシンエスビ', 'other'],
]

// 入金行の摘要から収入カテゴリを推定する。一致しなければ「その他収入」へ。
// 「決算お利息 8月分」（普通預金の利息）や Visaデビットのキャッシュバックがここに来る。
const BANK_INCOME_CATEGORY: ReadonlyArray<readonly [string, string]> = [
  ['給与', 'salary'], ['給料', 'salary'], ['キユウヨ', 'salary'], ['キュウヨ', 'salary'],
  ['賞与', 'salary'], ['ボーナス', 'salary'],
  ['利息', 'income_other'], ['配当', 'income_other'], ['還付', 'income_other'], ['返金', 'income_other'],
  ['キャッシュバック', 'income_other'], ['キヤシユバツク', 'income_other'],
  ['給付', 'income_other'], ['キユウフキン', 'income_other'], ['キュウフキン', 'income_other'],
]

interface ColumnMap {
  year: number
  month: number
  day: number
  desc: number
  withdrawal: number
  deposit: number
  note: number
}

// ヘッダー行から各列の位置を特定する。見つからない列は既定の並び順にフォールバックする。
// 「操作日(年)」「操作日(月)」はどれも「日」を含むので、年月日は括弧付きの単位で見分ける。
// 括弧が全角の書き出しもあり得るため、NFKC正規化してから突き合わせる。
function locateColumns(header: string[]): ColumnMap {
  const cells = header.map((h) => normalizeForMatch(h))
  const find = (match: (h: string) => boolean, fallback: number) => {
    const idx = cells.findIndex(match)
    return idx >= 0 ? idx : fallback
  }
  // 「操作時刻(時)」を「操作日(月)」等と取り違えないよう、時刻の列は除外する
  const isDatePart = (h: string, unit: string) => h.includes(`(${unit})`) && !h.includes('時刻')
  return {
    year: find((h) => isDatePart(h, '年'), 0),
    month: find((h) => isDatePart(h, '月'), 1),
    day: find((h) => isDatePart(h, '日'), 2),
    desc: find((h) => h.includes('摘要') || h.includes('内容'), 7),
    withdrawal: find((h) => h.includes('支払') || h.includes('出金'), 8),
    deposit: find((h) => h.includes('預り') || h.includes('預かり') || h.includes('入金'), 9),
    note: find((h) => h.includes('メモ'), 11),
  }
}

// 口座名やダウンロード日時が先頭に付くCSVもあり得るため、ヘッダー行を先頭数行から探す。
function findHeaderIndex(lines: string[]): number {
  const limit = Math.min(lines.length, 10)
  for (let i = 0; i < limit; i++) {
    const cells = splitCsvLine(lines[i]).map((c) => normalizeForMatch(c))
    const hasDate = cells.some((c) => c.includes('操作日') || c.includes('取引日') || c.includes('日付'))
    const hasAmount = cells.some((c) => c.includes('支払') || c.includes('預り') || c.includes('出金') || c.includes('入金'))
    if (hasDate && hasAmount) return i
  }
  return 0
}

// 年・月・日の3列を1つの日付にまとめる。月日はゼロ埋めされていないので補ってから、
// 共通の normalizeDate（区切り無しの8桁を月日の妥当性まで見て解釈する）に渡す。
function composeDate(year: string, month: string, day: string): string | null {
  const y = (year ?? '').trim()
  if (!/^\d{4}$/.test(y)) return null
  const mo = (month ?? '').trim().padStart(2, '0')
  const d = (day ?? '').trim().padStart(2, '0')
  return normalizeDate(`${y}${mo}${d}`)
}

// 摘要は「振込 オオサワ　サトル」のように種別と相手先が空白で区切られ、
// 書き出しによって空白の数がぶれる。重複排除キーに摘要が含まれるので、
// 取り込みごとにぶれない形へ揃えてから使う（相手先名の中の全角空白は残す）。
function normalizeDescription(raw: string | undefined): string {
  return (raw ?? '').replace(/[ \t]+/g, ' ').trim()
}

// メモ列は値が無いとき "-" が入ることがある。
function normalizeNote(raw: string | undefined): string | undefined {
  const note = (raw ?? '').trim()
  if (note === '' || note === '-' || note === '－' || note === 'ー') return undefined
  return note
}

export function parsePayPayBankCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<PayPayBankCategoryRef>,
): PayPayBankParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const headerIndex = findHeaderIndex(lines)
  const col = locateColumns(splitCsvLine(lines[headerIndex]))

  const { expenseIds, incomeIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)
  const incomeSet = new Set(incomeIds)

  const rows: PayPayBankImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(headerIndex + 1)) {
    const cols = splitCsvLine(line)
    const date = composeDate(cols[col.year] ?? '', cols[col.month] ?? '', cols[col.day] ?? '')
    const withdrawal = parseAmount(cols[col.withdrawal] ?? '')
    const deposit = parseAmount(cols[col.deposit] ?? '')

    // お支払金額・お預り金額のどちらかに正の金額がある行だけを取引として扱う
    const isIncome = Number.isFinite(deposit) && deposit > 0
    const amount = isIncome ? deposit : withdrawal
    if (!date || !Number.isFinite(amount) || amount <= 0) {
      ignored++
      continue
    }

    const description = normalizeDescription(cols[col.desc]) || 'PayPay銀行'
    const category = isIncome
      ? matchKeywordCategory(description, BANK_INCOME_CATEGORY, incomeSet) ?? fallbackIncome
      : matchKeywordCategory(description, BANK_EXPENSE_OVERRIDE, expenseSet)
        ?? matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet)
        ?? fallbackExpense

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category,
      description,
      amount,
      accountId,
      currency: 'JPY',
      note: normalizeNote(cols[col.note]),
    })
  }

  return { rows, parsed: rows.length, ignored }
}
